import { AfterViewInit, Component, EventEmitter, Input, Output } from "@angular/core";
import { Fee, Fiat, Field, PayoutAccount, Provider } from "@models";
import { UserService } from "@main-services/user.service";
import { PayAccountService } from "@main-services/pay_account.service";
import { FiatService } from "@main-services/currency/fiat.service";
import { truncateFixed } from "@helpers/utils";
import { AccountService } from "@modules/account/services/account.service";
import { MasterdataService } from "@core-services/masterdata.service";
import { TranslationService } from "@core-services/translation.service";

@Component({
	selector: "payout-tile",
	templateUrl: "./payout-tile.component.html",
	styleUrls: ["./payout-tile.component.scss"],
	standalone: false
})
export class PayoutTileComponent implements AfterViewInit {
	@Input()
	public account: PayoutAccount = null;
	@Input()
	public provider: Provider = null;
	@Input()
	public canDisable: boolean = false;
	@Input()
	public disabled: boolean = false;
	@Input()
	public currentFiat: Fiat = null;
	@Input()
	public active: boolean = false;

	@Input()
	public addAllowed = false;
	@Input()
	public deleteAllowed = false;

	@Input()
	public stacks = 1;

	@Output()
	public editAccount = new EventEmitter<PayoutAccount>();
	@Output()
	public createAccount = new EventEmitter<PayoutAccount>();
	@Output()
	public deleteAccount = new EventEmitter<PayoutAccount>();

	@Output()
	public onClicked = new EventEmitter();

	@Input()
	public tabIndex: number;
	@Input()
	public logo: string = null;

	private _bankName: string = "";
	private _holderName: string = "";

	public constructor(
		protected masterdataService: MasterdataService,
		protected translationService: TranslationService,
		protected payoutservice: PayAccountService,
		protected fiatService: FiatService,
		protected userService: UserService,
		protected accountService: AccountService
	) {}

	public ngAfterViewInit(): void {
		if (this.isSepa() && this.account) {
			this._bankName = this.account.getField("bank_name").getValue();
			this._holderName = this.account.getField("payout_receiver_name").getValue();
		}
	}

	public logoUrl(p: PayoutAccount | Provider): string {
		return this.masterdataService.logoUrl(p);
	}

	public get primaryField(): Field {
		return this.payoutservice.getPrimaryFieldForAccount(this.account);
	}

	public getPrimaryFieldValue(): string {
		return this.primaryField.getValue();
	}

	public tileClicked(): void {
		if (this.getAvailable()) {
			this.onClicked.emit();
		}
	}

	public getAvailable(): boolean {
		if (this.disabled) {
			return false;
		}
		if (!this.canDisable) {
			return true;
		}

		if (this.data instanceof Provider) {
			const provider = this.data;
			if (provider.isUnavailable()) {
				return false;
			}

			if (this.fiats.length === 0) {
				return false;
			}

			if (provider.needsVerification() && !this.userService.user.isVerified()) {
				return false;
			}
		}

		return true;
	}

	public tooltipText(): string {
		if (this.provider != null) {
			if (this.provider.isUnavailable()) {
				return "payout_tile_tooltip_not_available_because_blocked";
			}

			if (this.fiats.length === 0 || !this.provider.hasFiat(this.fiat)) {
				return "payout_tile_tooltip_not_available_because_of_fiat";
			}
		}
		return "";
	}

	public add(): void {
		this.createAccount.emit(this.account);
	}

	public delete(): void {
		if (this.deleteAllowed) {
			this.deleteAccount.emit(this.account);
		}
	}

	public get fiats(): Fiat[] {
		return this.masterdataService.getAvailableFiats(this.providerForced);
	}

	public hasFees(): boolean {
		const fee = this.getFee();
		return !(
			fee == null ||
			(fee.getFeeMax() === 0 && fee.getFeePercent() === 0 && fee.getFeeFix() === 0) ||
			this.feeString().length === 0
		);
	}

	private getFee(): Fee {
		if (this.account != null) {
			return this.masterdataService
				.getPayoutProviderById(this.account.getPayoutProviderId())
				.getFeeByFiatId(this.account.getFiatId());
		}

		return this.provider.getFeeByFiatId(this.fiat.getId());
	}

	public feeString(): string {
		let feeStr = "";
		const fee = this.providerForced.getFeeByFiatId(this.fiat.getId());

		if (fee.getFeeFix() > 0) {
			feeStr += this.round(fee.getFeeFix()) + " " + this.fiat.getSymbol();
		}

		if (fee.getFeePercent() > 0) {
			feeStr += " + " + this.round(fee.getFeePercent()) + "%";
		}

		if (fee.getFeeMax() > 0) {
			feeStr +=
				" (" +
				this.translationService.translate("fee_max") +
				" " +
				this.round(fee.getFeeMax()) +
				" " +
				this.fiat.getSymbol() +
				")";
		}

		return feeStr;
	}

	public get data(): PayoutAccount | Provider {
		if (this.account != null) {
			return this.account;
		}
		return this.provider;
	}

	private round(max: number): string {
		return truncateFixed(max, this.fiat.getPrecision());
	}

	public get providerForced(): Provider {
		if (this.provider != null) {
			return this.provider;
		}
		return this.masterdataService.getPayoutProviderById(this.account.getPayoutProviderId());
	}

	public get fiat(): Fiat {
		let fiat = this.currentFiat;
		if (fiat == null && this.account == null) {
			fiat = this.accountService.getDefaultFiat();
		}
		if (fiat == null && this.account != null) {
			fiat = this.fiatService.getItemById(this.account.getFiatId());
		}

		return fiat;
	}

	public get isEditAble(): boolean {
		if (this.account == null || this.account.isCard()) {
			return false;
		}
		return this.account.getFields().length > 0;
	}

	public edit(): void {
		this.editAccount.emit(this.account);
	}

	public get fiatsAvailable(): string {
		const fees = this.provider.getFees();
		const availableFiats: string[] = this.fiats
			.filter((fiat: Fiat) => fees.some((fee: Fee) => fee.getFiatId() === fiat.getId()))
			.map((fiat: Fiat) => fiat.getSymbol());

		const and: string = this.translationService.translate("and");
		if (availableFiats.length === 1) {
			return availableFiats[0];
		}
		return (
			availableFiats.splice(0, availableFiats.length - 1).join(", ") +
			" " +
			and +
			" " +
			availableFiats[availableFiats.length - 1]
		);
	}

	public getName(): string {
		return this.providerForced.getName();
	}

	public get isStacked(): boolean {
		return this.stacks > 1;
	}

	public showDescription(): boolean {
		return (!this.account && !this.isStacked) || this.isAmazon() || (this.isSepa() && this.isStacked);
	}

	public getDescription(): string {
		return this.providerForced.getMultiLangField("description", this.lang);
	}

	public isInstant(): boolean {
		return this.providerForced.isInstant();
	}

	public getBankName(): string {
		return this._bankName;
	}

	public getHolderName(): string {
		return this._holderName;
	}

	public showAction(): boolean {
		return (!this.account && !this.isStacked) || this.isAmazon();
	}

	public isSepa(): boolean {
		return this.providerForced.isShortNameSepa();
	}

	public isAmazon(): boolean {
		return this.providerForced.isShortNameAmazon();
	}

	private get lang(): string {
		return this.userService.user.getLanguage();
	}
}
